import { useEffect, useState } from "react";
import type {
  PhysicsConfig,
  SnowConfig,
  WaveGroundConfig,
  WatchHandsConfig,
  Css3dConfig,
  ScrollCameraConfig,
  RainbowDiamondConfig,
} from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { CompactCheck, CompactColor, CompactNumber, CompactSlider, CompactText, MiniBtn, Row } from "../../inspector/rows";

/** Two ScrollCameraConfig fields that exist only in the contract and in
 *  NichePlugins' defaults literal. */
import { promptDialog } from "@/shared/ui";

/** Uniform failure text for the action buttons below, which used to fire their
 *  RPC bare (`async () => setX(await rpc())`) and lose the rejection entirely. */
const errText = (what: string, e: unknown) => `${what} failed: ${e instanceof Error ? e.message : String(e)}`;

/**
 * Wave J — decorative/niche plugin controls (iJewel utility_plugins). Each
 * round-trips through its engine get/set RPC; action buttons call the
 * corresponding RPC. Labels/defaults are transcribed verbatim from the captured
 * iJewel schema. All OFF by default; Rainbow is gem-only.
 */

// ── Physics ──────────────────────────────────────────────────────────────────
export function PhysicsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<PhysicsConfig | null>(null);
  const [bodies, setBodies] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { r.getPhysicsConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<PhysicsConfig>) => r.setPhysicsConfig(p).then(setC).catch(() => {});
  return (
    <>
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="stepCount"><CompactNumber value={c.stepCount} min={1} step={1} width={76} onChange={(v) => void set({ stepCount: v })} /></Row>
      <Row label="delta"><CompactNumber value={c.delta} min={0.001} step={0.01} width={76} onChange={(v) => void set({ delta: v })} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="precision_manufacturing"
          busy={busy}
          onClick={async () => {
            setErr(null);
            setBusy(true);
            try { setBodies(await r.physicsMakeRootBodies()); }
            catch (e) { setErr(errText("Make Root Bodies", e)); }
            finally { setBusy(false); }
          }}
        >
          {busy ? "Building…" : "Make Root Bodies"}
        </MiniBtn>
        <MiniBtn icon="skip_next" disabled={busy} onClick={() => { setErr(null); void r.physicsStep().catch((e) => setErr(errText("Step", e))); }}>step</MiniBtn>
      </div>
      {err ? <div className="px-3 pb-2 text-[11px] text-ed-red">{err}</div> : null}
      {bodies != null ? <div className="px-3 pb-2 text-[11px] text-ed-text-3">{bodies} body(ies) created. Enable to run continuously, or use step.</div> : null}
    </>
  );
}

// ── Snow Fall Particles ──────────────────────────────────────────────────────
export function SnowControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<SnowConfig | null>(null);
  useEffect(() => { r.getSnowConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<SnowConfig>) => r.setSnowConfig(p).then(setC).catch(() => {});
  return (
    <>
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Bounds size"><CompactSlider value={c.maxRange} min={1} max={50} step={1} onChange={(v) => void set({ maxRange: v })} /></Row>
      <Row label="Particle size"><CompactSlider value={c.particleSize} min={0.01} max={2} step={0.01} onChange={(v) => void set({ particleSize: v })} /></Row>
      <Row label="Particle speed"><CompactSlider value={c.particleSpeed} min={0} max={2} step={0.01} onChange={(v) => void set({ particleSpeed: v })} /></Row>
      <Row label="Bottom clamp"><CompactSlider value={c.minRangeY} min={0} max={30} step={0.1} onChange={(v) => void set({ minRangeY: v })} /></Row>
      <Row label="Paused"><CompactCheck checked={c.paused} onChange={(v) => void set({ paused: v })} /></Row>
    </>
  );
}

// ── Wave Ground ──────────────────────────────────────────────────────────────
export function WaveGroundControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<WaveGroundConfig | null>(null);
  useEffect(() => { r.getWaveGroundConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<WaveGroundConfig>) => r.setWaveGroundConfig(p).then(setC).catch(() => {});
  return (
    <>
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Noise scale"><CompactSlider value={c.noiseScale} min={0} max={2} step={0.01} onChange={(v) => void set({ noiseScale: v })} /></Row>
      <Row label="Amplitude"><CompactSlider value={c.amplitude} min={0} max={2} step={0.01} onChange={(v) => void set({ amplitude: v })} /></Row>
      <Row label="Paused"><CompactCheck checked={c.paused} onChange={(v) => void set({ paused: v })} /></Row>
    </>
  );
}

// ── Watch Hands Time ─────────────────────────────────────────────────────────
export function WatchHandsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<WatchHandsConfig | null>(null);
  const [matched, setMatched] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { r.getWatchHandsConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<WatchHandsConfig>) => r.setWatchHandsConfig(p).then(setC).catch(() => {});
  return (
    <>
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Invert axis"><CompactCheck checked={c.invertAxis} onChange={(v) => void set({ invertAxis: v })} /></Row>
      <Row label="Analog"><CompactCheck checked={c.analog} onChange={(v) => void set({ analog: v })} /></Row>
      <Row label="Hour offset"><CompactSlider value={c.hourOffset} min={0} max={12} step={0.001} onChange={(v) => void set({ hourOffset: v })} /></Row>
      <Row label="Minute offset"><CompactSlider value={c.minuteOffset} min={0} max={60} step={0.001} onChange={(v) => void set({ minuteOffset: v })} /></Row>
      <Row label="Second offset"><CompactSlider value={c.secondOffset} min={0} max={60} step={0.001} onChange={(v) => void set({ secondOffset: v })} /></Row>
      <Row label="Hour match"><CompactText value={c.hour} onChange={(v) => void set({ hour: v })} /></Row>
      <Row label="Minute match"><CompactText value={c.minute} onChange={(v) => void set({ minute: v })} /></Row>
      <Row label="Second match"><CompactText value={c.second} onChange={(v) => void set({ second: v })} /></Row>
      <Row label="Regex"><CompactCheck checked={c.regex} onChange={(v) => void set({ regex: v })} /></Row>
      <div className="flex items-center gap-2 px-3 py-1.5">
        <MiniBtn
          icon="schedule"
          primary
          busy={busy}
          onClick={async () => {
            setErr(null);
            setBusy(true);
            try { setMatched(await r.refreshWatchHands()); }
            catch (e) { setErr(errText("Refresh", e)); }
            finally { setBusy(false); }
          }}
        >
          Refresh
        </MiniBtn>
        {matched != null ? <span className="text-[12px] text-ed-text-3">{matched} hand(s) matched</span> : null}
      </div>
      {err ? <div className="px-3 pb-2 text-[11px] text-ed-red">{err}</div> : null}
    </>
  );
}

// ── CSS3D Renderer ───────────────────────────────────────────────────────────
export function Css3dControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<Css3dConfig | null>(null);
  const [busy, setBusy] = useState<null | "iframe" | "card">(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { r.getCss3dConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<Css3dConfig>) => r.setCss3dConfig(p).then(setC).catch(() => {});

  const attach = async (which: "iframe" | "card", url?: string) => {
    setErr(null);
    setMsg(null);
    setBusy(which);
    try {
      const ok = await r.attachIframeToSelected(url);
      // The boolean return was discarded: attach fails (returns false) when there
      // is no CSS3D scene / nothing selected, which looked identical to success.
      if (!ok) { setErr("Nothing attached — select an object first."); return; }
      setMsg(which === "iframe" ? "iframe attached." : "HTML card attached.");
      // The viewer force-enables CSS3D on attach (NichePlugins.attachIframeToSelected),
      // so re-read the config or the "Enabled" switch keeps showing off while it runs.
      setC(await r.getCss3dConfig());
    } catch (e) {
      setErr(errText("Attach", e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Over canvas"><CompactCheck checked={c.overCanvas} onChange={(v) => void set({ overCanvas: v })} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        {/* Split in two: the single prompt-driven button treated Cancel
            (`prompt() || undefined`) exactly like a blank URL, so dismissing the
            dialog still attached an HTML card to the scene. */}
        <MiniBtn
          icon="web"
          busy={busy === "iframe"}
          disabled={busy !== null && busy !== "iframe"}
          onClick={() => {
            void promptDialog({ title: "Attach iframe", label: "iframe URL" }).then((u) => {
              if (u == null) return;
              return attach("iframe", u);
            });
          }}
        >
          Attach iframe
        </MiniBtn>
        <MiniBtn icon="note" busy={busy === "card"} disabled={busy !== null && busy !== "card"} onClick={() => void attach("card")}>Attach HTML card</MiniBtn>
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      {msg ? <div className="px-3 pb-1 text-[11px] text-ed-text-3">{msg}</div> : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">Attaches at the selected object's position in a CSS3D layer over the canvas.</div>
    </>
  );
}

// ── Scrollable Camera View (Dev) ─────────────────────────────────────────────
export function ScrollCameraControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<ScrollCameraConfig | null>(null);
  const [code, setCode] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { r.getScrollCameraConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<ScrollCameraConfig>) => r.setScrollCameraConfig(p).then(setC).catch(() => {});
  return (
    <>
      <Row label="Enabled"><CompactCheck checked={c.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Enable Damping"><CompactCheck checked={c.enableDamping} onChange={(v) => void set({ enableDamping: v })} /></Row>
      <Row label="Damping"><CompactSlider value={c.damping} min={0.01} max={1} step={0.001} onChange={(v) => void set({ damping: v })} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="code"
          busy={busy}
          onClick={async () => {
            setErr(null);
            setBusy(true);
            try { setCode(await r.generateScrollCode()); }
            catch (e) { setErr(errText("Generate code", e)); }
            finally { setBusy(false); }
          }}
        >
          Show/Edit Code
        </MiniBtn>
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      {code ? <pre className="mx-3 mb-2 max-h-32 overflow-auto rounded bg-ed-panel-2 p-2 text-[11px] text-ed-text-2 whitespace-pre-wrap">{code}</pre> : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">Dev preview: scroll over the viewer to orbit; damping smooths the motion.</div>
    </>
  );
}

// ── Widgets (helper gizmo visibility) ────────────────────────────────────────
export function WidgetsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [visible, setVisible] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { r.getWidgetsConfig().then((w) => setVisible(w.visible)).catch(() => {}); }, [r]);
  return (
    <>
      <Row label="Visible">
        <CompactCheck
          checked={visible}
          onChange={(v) => {
            setVisible(v);
            setErr(null);
            // Optimistic switch — revert it if the viewer refuses, so the UI
            // never claims the gizmos are showing when they are not.
            void r.setWidgetsConfig({ visible: v }).catch((e) => { setVisible(!v); setErr(errText("Widgets", e)); });
          }}
        />
      </Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn icon="refresh" onClick={() => { setErr(null); void r.refreshWidgets().catch((e) => setErr(errText("Refresh", e))); }}>Refresh</MiniBtn>
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">Toggles the scene light/transform helper gizmos.</div>
    </>
  );
}

// ── UV Unwrapping (xAtlas) ───────────────────────────────────────────────────
export function XAtlasControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [msg, setMsg] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="grid_4x4"
          primary
          busy={busy}
          onClick={async () => {
            setErr(null);
            setMsg("");
            setBusy(true);
            // Unwrapping walks the whole selected geometry — long enough to be
            // double-clicked, and it was fired with neither a pending state nor
            // a catch.
            try { setMsg((await r.unwrapSelectedUVs()) ? "UVs generated ✓" : "Select a mesh first"); }
            catch (e) { setErr(errText("Unwrap", e)); }
            finally { setBusy(false); }
          }}
        >
          Unwrap Selected Geometry
        </MiniBtn>
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      {msg ? <div className="px-3 pb-1 text-[12px] text-ed-text-3">{msg}</div> : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">Built-in box-projection unwrap (writes a uv + uv2 channel). Full xAtlas chart packing needs a wasm dep.</div>
    </>
  );
}

// ── Rainbow Diamond Coloring (gem-only) ──────────────────────────────────────
export function RainbowDiamondControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<RainbowDiamondConfig | null>(null);
  const [colored, setColored] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { r.getRainbowDiamondConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (p: Partial<RainbowDiamondConfig>) => r.setRainbowDiamondConfig(p).then(setC).catch(() => {});
  return (
    <>
      <Row label="Object name prefix"><CompactText value={c.prefix} placeholder="(all stones)" onChange={(v) => void set({ prefix: v })} /></Row>
      <Row label="Count"><CompactNumber value={c.count} min={1} step={1} width={76} onChange={(v) => void set({ count: v })} /></Row>
      <Row label="Loops"><CompactNumber value={c.loops} min={1} step={1} width={76} onChange={(v) => void set({ loops: v })} /></Row>
      <Row label="First Color"><CompactColor value={c.firstColor} onChange={(v) => void set({ firstColor: v })} /></Row>
      <div className="flex items-center gap-2 px-3 py-1.5">
        <MiniBtn
          icon="palette"
          primary
          busy={busy}
          onClick={async () => {
            setErr(null);
            setBusy(true);
            try { setColored(await r.applyRainbowDiamond()); }
            catch (e) { setErr(errText("Color All Items", e)); }
            finally { setBusy(false); }
          }}
        >
          Color All Items
        </MiniBtn>
        {colored != null ? <span className="text-[12px] text-ed-text-3">{colored} stone(s) colored</span> : null}
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">Tints gem stones only (the diamond shader body color) — never metals.</div>
    </>
  );
}
